"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import { saveToIndexedDB, getFromIndexedDB, deleteFromIndexedDB } from "@/src/utils/indexed-db-helper"

/**
 * Base interface all wizard states must extend
 * Provides common fields for validation and debugging
 */
export interface BaseWizardState {
  /** Wizard type identifier (e.g., "add-canvas", "upload-room") */
  wizardType: string
  /** Timestamp when state was saved (for staleness detection) */
  timestamp: number
  /** User ID who created this state (for security validation) */
  userId: string
  /** Current step in the wizard */
  currentStep: number
}

/**
 * Configuration for wizard state preservation
 */
export interface WizardStateConfig<T extends BaseWizardState> {
  /** Unique identifier for this wizard (used as storage key) */
  wizardId: string
  /** Current state object to preserve (watched for changes) */
  state: T
  /** Enable/disable automatic state preservation (default: true) */
  enabled?: boolean
  /** Callback fired after state is restored */
  onRestore?: (restoredState: T) => void
  /** Validate restored state before using it (return false to reject) */
  validateState?: (state: T) => boolean
  /** Custom serialization for complex types */
  serialize?: (state: T) => string
  deserialize?: (json: string) => T
}

/**
 * Return type for useWizardStatePreservation hook
 */
export interface WizardStatePreservationReturn {
  /** True while restoration is in progress */
  isRestoring: boolean
  /** True if state was successfully restored on mount */
  hasRestoredState: boolean
  /** Manually clear saved state */
  clearSavedState: () => void
  /** Callback to pass to BillingErrorComponent (saves state before redirect) */
  onBeforePurchase: () => void
  /** The restored state object (null if no saved state exists) */
  restoredState: any | null
}

/**
 * Enhanced wizard state preservation hook with automatic tracking
 *
 * Features:
 * - Automatic state saving (debounced)
 * - Automatic restoration on mount
 * - Built-in validation and staleness detection
 * - Standardized interface across all wizards
 * - Type-safe with generics
 *
 * @template T - The wizard's state shape (must extend BaseWizardState)
 * @param config - Configuration object
 * @returns Preservation utilities and restoration status
 */
export function useWizardStatePreservation<T extends BaseWizardState>(
  config: WizardStateConfig<T>,
): WizardStatePreservationReturn {
  const [isRestoring, setIsRestoring] = useState(true)
  const [hasRestoredState, setHasRestoredState] = useState(false)
  const [restoredState, setRestoredState] = useState<T | null>(null)
  const hasRestoredRef = useRef(false)
  const storageKey = `wizard_state_${config.wizardId}`

  useEffect(() => {
    if (hasRestoredRef.current) {
      console.log(`[v0] ${config.wizardId}: Already restored, skipping`)
      setIsRestoring(false)
      return
    }

    const restoreState = async () => {
      console.log(`[v0] ${config.wizardId}: Checking for saved state...`)

      let sessionStorageState: T | null = null
      let indexedDBState: T | null = null

      // Try sessionStorage
      const savedJson = sessionStorage.getItem(storageKey)
      if (savedJson) {
        try {
          sessionStorageState = config.deserialize ? config.deserialize(savedJson) : (JSON.parse(savedJson) as T)
        } catch (e) {
          console.error(`[v0] ${config.wizardId}: Failed to parse sessionStorage state:`, e)
        }
      }

      // Also check IndexedDB
      const indexedDBData = await getFromIndexedDB(storageKey)
      if (indexedDBData) {
        try {
          const dataStr = typeof indexedDBData === "string" ? indexedDBData : JSON.stringify(indexedDBData)
          indexedDBState = config.deserialize ? config.deserialize(dataStr) : (JSON.parse(dataStr) as T)
        } catch (e) {
          console.error(`[v0] ${config.wizardId}: Failed to parse IndexedDB state:`, e)
        }
      }

      // Prefer the state with pendingPurchase flag (indicates Stripe redirect)
      let restored: T | null = null
      let storageType = ""

      const sessionHasPending = sessionStorageState && (sessionStorageState as any).pendingPurchase === true
      const indexedDBHasPending = indexedDBState && (indexedDBState as any).pendingPurchase === true

      if (indexedDBHasPending) {
        // IndexedDB has pending purchase (likely saved there due to quota)
        restored = indexedDBState
        storageType = "IndexedDB"
      } else if (sessionHasPending) {
        // sessionStorage has pending purchase
        restored = sessionStorageState
        storageType = "sessionStorage"
      } else if (sessionStorageState) {
        // No pending purchase - use sessionStorage state (will be rejected below)
        restored = sessionStorageState
        storageType = "sessionStorage"
      } else if (indexedDBState) {
        // No pending purchase - use IndexedDB state (will be rejected below)
        restored = indexedDBState
        storageType = "IndexedDB"
      }

      if (!restored) {
        console.log(`[v0] ${config.wizardId}: No saved state found`)
        setIsRestoring(false)
        return
      }

      console.log(`[v0] ${config.wizardId}: Found saved state in ${storageType}`, {
        timestamp: new Date(restored.timestamp).toISOString(),
        age: `${Math.round((Date.now() - restored.timestamp) / 1000)}s`,
        hasPendingPurchase: !!(restored as any).pendingPurchase,
      })

      const hasPendingPurchase = (restored as any).pendingPurchase === true

      if (hasPendingPurchase) {
        // Check URL for checkout=success parameter
        const urlParams = new URLSearchParams(window.location.search)
        const isComingFromStripe = urlParams.get("checkout") === "success"

        if (!isComingFromStripe) {
          console.log(
            `[v0] ${config.wizardId}: Found pendingPurchase flag but no checkout=success in URL - clearing old state`,
          )
          sessionStorage.removeItem(storageKey)
          await deleteFromIndexedDB(storageKey)
          setIsRestoring(false)
          return
        }

        console.log(
          `[v0] ${config.wizardId}: Pending purchase flag found and checkout=success confirmed - restoring state from Stripe redirect`,
        )
      } else {
        console.log(`[v0] ${config.wizardId}: No pending purchase flag - user navigated away normally, starting fresh`)
        sessionStorage.removeItem(storageKey)
        await deleteFromIndexedDB(storageKey)
        setIsRestoring(false)
        return
      }

      // Validate staleness (default: 10 minutes)
      const maxAge = 10 * 60 * 1000
      const age = Date.now() - restored.timestamp
      if (age > maxAge) {
        console.warn(`[v0] ${config.wizardId}: State too old (${Math.round(age / 1000)}s), rejecting`)
        sessionStorage.removeItem(storageKey)
        await deleteFromIndexedDB(storageKey)
        setIsRestoring(false)
        return
      }

      // Custom validation
      if (config.validateState && !config.validateState(restored)) {
        console.warn(`[v0] ${config.wizardId}: State failed validation, rejecting`)
        sessionStorage.removeItem(storageKey)
        await deleteFromIndexedDB(storageKey)
        setIsRestoring(false)
        return
      }

      delete (restored as any).pendingPurchase

      // State is valid - restore it
      setRestoredState(restored)
      setHasRestoredState(true)
      hasRestoredRef.current = true

      // Call restoration callback
      if (config.onRestore) {
        console.log(`[v0] ${config.wizardId}: Calling onRestore callback`)
        config.onRestore(restored)
      }

      // Clean up saved state after successful restoration
      sessionStorage.removeItem(storageKey)
      await deleteFromIndexedDB(storageKey)
      console.log(`[v0] ${config.wizardId}: ✅ State restored successfully and cleaned up`)

      // This ensures the hook can check for checkout=success before it's removed
      const urlParams = new URLSearchParams(window.location.search)
      if (urlParams.get("checkout") === "success") {
        const cleanUrl = new URL(window.location.href)
        cleanUrl.search = "" // Clear all query params
        window.history.replaceState({}, "", cleanUrl.pathname)
        console.log(`[v0] ${config.wizardId}: 🧹 Cleaned URL after successful restoration`)
      }

      setIsRestoring(false)
    }

    restoreState()
  }, []) // Only run on mount

  useEffect(() => {
    if (!config.enabled && config.enabled !== undefined) {
      console.log(`[v0] ${config.wizardId}: State preservation disabled`)
      return
    }

    // Don't save during restoration
    if (isRestoring) {
      return
    }

    // Debounce saves to avoid excessive writes
    const timeoutId = setTimeout(() => {
      const saveState = async () => {
        try {
          const serialized = config.serialize ? config.serialize(config.state) : JSON.stringify(config.state)
          const sizeInKB = new Blob([serialized]).size / 1024

          if (sizeInKB > 5000) {
            console.warn(`[v0] ${config.wizardId}: State size is ${sizeInKB.toFixed(2)} KB, may exceed quota!`)
          }

          try {
            // Try sessionStorage first (fast)
            sessionStorage.setItem(storageKey, serialized)
            console.log(`[v0] ${config.wizardId}: Auto-saved state to sessionStorage (${sizeInKB.toFixed(2)} KB)`)
          } catch (quotaError: any) {
            if (quotaError.name === "QuotaExceededError") {
              // Fallback to IndexedDB for large images
              console.warn(`[v0] ${config.wizardId}: sessionStorage quota exceeded, falling back to IndexedDB`)
              await saveToIndexedDB(storageKey, serialized)
              console.log(`[v0] ${config.wizardId}: Auto-saved state to IndexedDB (${sizeInKB.toFixed(2)} KB)`)
            } else {
              throw quotaError
            }
          }
        } catch (error) {
          console.error(`[v0] ${config.wizardId}: Failed to auto-save state:`, error)
        }
      }

      saveState()
    }, 500) // 500ms debounce

    return () => clearTimeout(timeoutId)
  }, [config.state, config.enabled, isRestoring])

  const clearSavedState = useCallback(async () => {
    sessionStorage.removeItem(storageKey)
    await deleteFromIndexedDB(storageKey)
    console.log(`[v0] ${config.wizardId}: Manually cleared saved state from both storages`)
  }, [storageKey, config.wizardId])

  const onBeforePurchase = useCallback(async () => {
    try {
      console.log(`[v0] ${config.wizardId}: 🔍 State before saving:`, {
        hasUploadedImage: !!(config.state as any).uploadedImage,
        uploadedImageSize: (config.state as any).uploadedImage?.length || 0,
        currentStep: config.state.currentStep,
        userId: config.state.userId,
      })

      const stateWithFlag = { ...config.state, pendingPurchase: true }
      const serialized = config.serialize ? config.serialize(stateWithFlag as T) : JSON.stringify(stateWithFlag)

      const sizeInKB = new Blob([serialized]).size / 1024
      console.log(`[v0] ${config.wizardId}: 📦 Serialized state size: ${sizeInKB.toFixed(2)} KB`)

      try {
        // Try sessionStorage first
        sessionStorage.setItem(storageKey, serialized)
        console.log(
          `[v0] ${config.wizardId}: 💾 Saved state with pendingPurchase flag to sessionStorage before Stripe redirect`,
        )
      } catch (quotaError: any) {
        if (quotaError.name === "QuotaExceededError") {
          // Fallback to IndexedDB
          console.warn(`[v0] ${config.wizardId}: sessionStorage quota exceeded, saving to IndexedDB instead`)
          await saveToIndexedDB(storageKey, serialized)
          console.log(
            `[v0] ${config.wizardId}: 💾 Saved state with pendingPurchase flag to IndexedDB before Stripe redirect`,
          )
        } else {
          throw quotaError
        }
      }
    } catch (error) {
      console.error(`[v0] ${config.wizardId}: Failed to save state before purchase:`, error)
    }
  }, [config.state, config.serialize, storageKey, config.wizardId])

  return {
    isRestoring,
    hasRestoredState,
    clearSavedState,
    onBeforePurchase,
    restoredState,
  }
}

/**
 * Convert File object to base64 string
 * Handles quota errors automatically
 */
export async function serializeImage(file: File): Promise<string | null> {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = (error) => {
      console.error("[v0] Failed to serialize image:", error)
      resolve(null)
    }
    reader.readAsDataURL(file)
  })
}

/**
 * Convert base64 string back to File object
 */
export function deserializeImage(base64: string, filename = "image.png"): File {
  const arr = base64.split(",")
  const mime = arr[0].match(/:(.*?);/)?.[1] || "image/png"
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new File([u8arr], filename, { type: mime })
}
